import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { LogoMarque } from '../marque/LogoMarque';
import { useAuth } from '../auth/useAuth';
import { appelApi, ErreurApi } from '../lib/api';
import type { UtilisateurConnecte } from '../lib/types';
import { useTitrePage } from '../lib/useTitrePage';

interface ApercuInvitation {
  prenom: string;
  email: string;
  libelleProfil: string;
}

/** Longueur exigée par l'API : une seule politique dans l'application. */
const LONGUEUR_MINIMALE = 12;

const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint';

/**
 * Activation d'un compte invité par la direction (TF-500) : la personne ouvre
 * le lien reçu par e-mail, choisit son mot de passe, et sa session s'ouvre.
 *
 * Hors zone privée, comme l'espace candidat : le jeton est lu dans l'adresse
 * puis transmis dans le corps des requêtes, jamais dans une URL d'API. Une
 * session déjà ouverte dans ce navigateur (la direction qui essaie le lien)
 * est d'abord fermée à la demande, plutôt que de renvoyer sur son tableau de
 * bord sans rien dire.
 */
export function ActivationComptePage(): JSX.Element {
  useTitrePage('Activation du compte');
  const { jeton = '' } = useParams();
  const { utilisateur, installerSession, deconnexion } = useAuth();

  const [apercu, setApercu] = useState<ApercuInvitation | null>(null);
  const [lienRefuse, setLienRefuse] = useState<string | null>(null);
  const [verification, setVerification] = useState(true);
  const [active, setActive] = useState(false);

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let annule = false;
    setVerification(true);

    appelApi<ApercuInvitation>('/invitations/verification', { methode: 'POST', corps: { jeton } })
      .then((reponse) => {
        if (!annule) setApercu(reponse);
      })
      .catch((cause) => {
        if (annule) return;
        setLienRefuse(
          cause instanceof ErreurApi ? cause.message : 'Ce lien n’a pas pu être vérifié. Réessayez plus tard.',
        );
      })
      .finally(() => {
        if (!annule) setVerification(false);
      });

    return () => {
      annule = true;
    };
  }, [jeton]);

  if (utilisateur && active) return <Navigate to="/dashboard" replace />;

  async function soumettre(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setErreur(null);

    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }
    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(`Le mot de passe doit faire au moins ${LONGUEUR_MINIMALE} caractères.`);
      return;
    }

    setEnCours(true);
    try {
      const reponse = await appelApi<{ utilisateur: UtilisateurConnecte }>('/invitations/activation', {
        methode: 'POST',
        corps: { jeton, motDePasse },
      });
      // La session est déjà ouverte par l'API : il n'y a qu'à la refléter.
      setActive(true);
      installerSession(reponse.utilisateur);
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi ? cause.message : 'Le compte n’a pas pu être activé. Réessayez plus tard.',
      );
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="login-bg flex min-h-screen items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-xl rounded-3xl bg-white p-4 shadow-2xl sm:p-8 md:p-10">
        <LogoMarque className="mb-8 h-9 w-auto" />

        {verification && <p className="text-sm text-codialis-gray">Vérification du lien…</p>}

        {lienRefuse && (
          <div>
            <h1 className="font-display text-2xl font-bold text-codialis-navy">Lien indisponible</h1>
            <p className="mt-2 text-sm text-codialis-gray">{lienRefuse}</p>
            <a
              href="/connexion"
              className="mt-6 inline-flex text-sm font-semibold text-codialis-sky hover:underline"
            >
              Aller à la page de connexion
            </a>
          </div>
        )}

        {apercu && utilisateur && !active && (
          <div>
            <h1 className="font-display text-2xl font-bold text-codialis-navy">Une session est déjà ouverte</h1>
            <p className="mt-2 text-sm text-codialis-gray">
              Vous êtes connecté(e) en tant que {utilisateur.email}. Déconnectez-vous pour activer le compte de{' '}
              {apercu.email}.
            </p>
            <button
              type="button"
              onClick={() => void deconnexion()}
              className="mt-6 w-full rounded-xl bg-codialis-navy px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              Se déconnecter
            </button>
          </div>
        )}

        {apercu && !utilisateur && (
          <>
            <h1 className="font-display text-2xl font-bold text-codialis-navy">Bienvenue {apercu.prenom}.</h1>
            <p className="mt-1 text-sm text-codialis-gray">
              Votre accès à la plateforme est prêt, profil « {apercu.libelleProfil} ». Choisissez votre mot de
              passe pour l’activer.
            </p>
            <p className="mt-4 rounded-2xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">
              Votre identifiant : <span className="font-semibold">{apercu.email}</span>
            </p>

            <form onSubmit={soumettre} className="mt-6 space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="motdepasse"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
                  >
                    Mot de passe
                  </label>
                  <input
                    id="motdepasse"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={LONGUEUR_MINIMALE}
                    value={motDePasse}
                    onChange={(evenement) => setMotDePasse(evenement.target.value)}
                    aria-describedby="regle-mot-de-passe"
                    className={CLASSE_CHAMP}
                  />
                  <p id="regle-mot-de-passe" className="mt-1 text-[11px] text-codialis-gray">
                    {LONGUEUR_MINIMALE} caractères au minimum.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="confirmation"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
                  >
                    Confirmation
                  </label>
                  <input
                    id="confirmation"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmation}
                    onChange={(evenement) => setConfirmation(evenement.target.value)}
                    className={CLASSE_CHAMP}
                  />
                </div>
              </div>

              {erreur && (
                <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {erreur}
                </p>
              )}

              <button
                type="submit"
                disabled={enCours}
                className="w-full rounded-xl bg-codialis-navy px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
              >
                {enCours ? 'Activation…' : 'Activer mon compte'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
